/*
 * Lior Apple Layer — fluid interaction for the King theme.
 * Principles: Apple "Designing Fluid Interfaces" (WWDC 2018), via the apple-design skill.
 *  - feedback on pointer-down, continuous during the gesture
 *  - 1:1 tracking that respects the grab offset
 *  - interruptible springs that start from the on-screen value and inherit release velocity
 *  - momentum projection decides dismiss vs. return
 *  - rubber-banding at boundaries, haptics only on meaningful commits
 * Progressive enhancement only: if this file fails to load, the theme behaves exactly as before.
 * No globals. Re-initialises on Shopify theme-editor section reloads.
 */
(() => {
  'use strict';

  const html = document.documentElement;
  if (html.hasAttribute('data-la-ready')) return;
  html.setAttribute('data-la-ready', '');

  // Options from the section's <script data-*> attributes (theme editor settings)
  const me = document.currentScript;
  const OPT = {
    drag: !me || me.dataset.laDrag !== 'false',
    haptics: !me || me.dataset.laHaptics !== 'false'
  };

  /* ------------------------------------------------------------------ *
   * Theme selectors (King theme, Lior build)
   * ------------------------------------------------------------------ */
  const SEL = {
    press: [
      '.button',
      'button:not([disabled])',
      '[role="button"]',
      'input[type="submit"]',
      '.header--icon-link-text',
      '.product--icon',
      '.drawer-close',
      '.popup-close',
      '.hamburger--toggler',
      '.variant--item',
      '[data-la-press]'
    ].join(','),
    pressCard: '.product--card-inner, [data-la-press-card]',
    cartCount: '[data-cart-count]',
    drawer: '.side-drawer',
    drawerPanel: '.side--drawer-panel',
    drawerOverlay: '.close-fullwidth',
    drawerClose: '[data-drawer-close]',
    popup: '.popup[data-popup-modal]',
    popupClose: '[data-popup-close]',
    noDrag: [
      'input', 'textarea', 'select', 'option', '[contenteditable]',
      '.swiper', '.swiper-wrapper', 'swiper-content', '[data-swiper]', '[data-swiper-main]',
      'input[type="range"]', '.no-la-drag', '[data-no-la-drag]', 'iframe', 'model-viewer'
    ].join(','),
    exclude: '.lmm, [data-lior-mm]' // Lior custom mobile menu keeps its own motion
  };

  const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reducedMotion = () => mqReduce.matches;

  /* ------------------------------------------------------------------ *
   * Physics primitives
   * ------------------------------------------------------------------ */

  // Apple's designer-facing spring: dampingRatio + response (seconds). Mass = 1.
  class Spring {
    constructor({ damping = 1, response = 0.4, value = 0, onUpdate, onRest } = {}) {
      this.setParams(damping, response);
      this.value = value;
      this.velocity = 0;
      this.target = value;
      this.onUpdate = onUpdate || (() => {});
      this.onRest = onRest || (() => {});
      this._raf = 0;
      this._last = 0;
      this._tick = this._tick.bind(this);
    }

    setParams(damping, response) {
      this.stiffness = Math.pow((2 * Math.PI) / response, 2);
      this.dampingCoeff = (4 * Math.PI * damping) / response;
    }

    // Retarget from the current presentation value. Velocity is carried unless given.
    to(target, { velocity, damping, response, onRest } = {}) {
      if (damping !== undefined || response !== undefined) this.setParams(damping ?? 1, response ?? 0.4);
      if (typeof velocity === 'number' && isFinite(velocity)) this.velocity = velocity;
      if (onRest) this.onRest = onRest;
      this.target = target;
      if (reducedMotion()) {
        this.stop();
        this.value = target;
        this.velocity = 0;
        this.onUpdate(this.value);
        this.onRest(this.value);
        return;
      }
      if (!this._raf) {
        this._last = performance.now();
        this._raf = requestAnimationFrame(this._tick);
      }
    }

    stop() {
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = 0;
    }

    _tick(now) {
      let dt = Math.min((now - this._last) / 1000, 1 / 30);
      this._last = now;
      const step = 1 / 240;
      while (dt > 0) {
        const h = Math.min(step, dt);
        const accel = -this.stiffness * (this.value - this.target) - this.dampingCoeff * this.velocity;
        this.velocity += accel * h;
        this.value += this.velocity * h;
        dt -= h;
      }
      if (Math.abs(this.value - this.target) < 0.3 && Math.abs(this.velocity) < 4) {
        this.value = this.target;
        this.velocity = 0;
        this._raf = 0;
        this.onUpdate(this.value);
        this.onRest(this.value);
        return;
      }
      this.onUpdate(this.value);
      this._raf = requestAnimationFrame(this._tick);
    }
  }

  // Samples a spring into WAAPI keyframes (one-shot animations: press release, badge pop).
  function springKeyframes(from, to, { damping = 1, response = 0.3, prop = 'scale' } = {}) {
    const k = Math.pow((2 * Math.PI) / response, 2);
    const c = (4 * Math.PI * damping) / response;
    const span = Math.abs(to - from) || 1;
    let x = from, v = 0, t = 0;
    const dt = 1 / 240;
    const pts = [];
    while (t < 2) {
      const a = -k * (x - to) - c * v;
      v += a * dt;
      x += v * dt;
      t += dt;
      pts.push(x);
      if (Math.abs(x - to) < 0.001 * span && Math.abs(v) < 0.01 * span) break;
    }
    const n = 24;
    const frames = [{ [prop]: String(from), offset: 0 }];
    for (let i = 1; i < n; i++) {
      const idx = Math.min(pts.length - 1, Math.round((i / n) * (pts.length - 1)));
      frames.push({ [prop]: String(+pts[idx].toFixed(4)), offset: i / n });
    }
    frames.push({ [prop]: String(to), offset: 1 });
    return { frames, duration: Math.max(120, t * 1000) };
  }

  // Short position history -> release velocity (px/s).
  class VelocityTracker {
    constructor() { this.samples = []; }
    reset() { this.samples.length = 0; }
    add(pos, t = performance.now()) {
      this.samples.push({ pos, t });
      while (this.samples.length > 2 && t - this.samples[0].t > 100) this.samples.shift();
    }
    velocity() {
      const s = this.samples;
      if (s.length < 2) return 0;
      const last = s[s.length - 1];
      if (performance.now() - last.t > 80) return 0; // finger rested before lifting
      const first = s[0];
      const dt = (last.t - first.t) / 1000;
      return dt > 0 ? (last.pos - first.pos) / dt : 0;
    }
  }

  // Apple's momentum projection (Designing Fluid Interfaces sample code).
  const project = (velocity, decelerationRate = 0.998) =>
    ((velocity / 1000) * decelerationRate) / (1 - decelerationRate);

  // Progressive resistance past a boundary.
  const rubberband = (overshoot, dimension, constant = 0.55) =>
    (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const nextFrame = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));

  const haptic = (ms = 8) => {
    if (!OPT.haptics) return;
    try { if (navigator.vibrate) navigator.vibrate(ms); } catch (_) { /* unsupported */ }
  };

  /* ------------------------------------------------------------------ *
   * 1. Press feedback — on pointer-down, instantly; cancelled by movement.
   *    The individual `scale` property via WAAPI composes with the theme's own
   *    transforms (magnet effect, hover zoom) and never overwrites its transitions.
   * ------------------------------------------------------------------ */
  const pressAnims = new WeakMap();

  const liveScale = (el) => {
    const s = getComputedStyle(el).scale;
    const n = parseFloat(s);
    return s === 'none' || !isFinite(n) ? 1 : n;
  };

  function pressIn(el, isCard) {
    const prev = pressAnims.get(el);
    if (reducedMotion()) {
      prev?.cancel();
      pressAnims.set(el, el.animate([{ opacity: 1 }, { opacity: 0.7 }], { duration: 80, fill: 'forwards' }));
      return;
    }
    const from = liveScale(el); // start from the presentation value (interruptible)
    prev?.cancel();
    pressAnims.set(el, el.animate(
      [{ scale: String(from) }, { scale: isCard ? '0.985' : '0.96' }],
      { duration: isCard ? 160 : 100, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'forwards' }
    ));
  }

  function pressOut(el) {
    const prev = pressAnims.get(el);
    if (reducedMotion()) {
      prev?.cancel();
      el.animate([{ opacity: 0.7 }, { opacity: 1 }], { duration: 160 });
      return;
    }
    const from = liveScale(el);
    prev?.cancel();
    const { frames, duration } = springKeyframes(from, 1, { damping: 1, response: 0.3 });
    const anim = el.animate(frames, { duration, easing: 'linear' });
    pressAnims.set(el, anim);
  }

  function initPress() {
    let pressed = null;
    let sx = 0, sy = 0;

    const release = () => {
      if (!pressed) return;
      const el = pressed;
      pressed = null;
      el.removeAttribute('data-la-pressed');
      pressOut(el);
    };

    document.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || e.isPrimary === false) return;
      const t = e.target;
      if (!(t instanceof Element) || t.closest(SEL.exclude)) return;
      let el = t.closest(SEL.press);
      let isCard = false;
      if (!el) { el = t.closest(SEL.pressCard); isCard = !!el; }
      if (!el || el.matches('[disabled], [aria-disabled="true"]')) return;
      if (getComputedStyle(el).display === 'inline') return; // transforms don't apply to inline boxes
      release();
      pressed = el;
      sx = e.clientX;
      sy = e.clientY;
      el.setAttribute('data-la-pressed', '');
      pressIn(el, isCard);
    }, { capture: true, passive: true });

    document.addEventListener('pointermove', (e) => {
      if (!pressed) return;
      // ~10px hysteresis: a scroll or drag that starts on a button is not a press
      if (Math.hypot(e.clientX - sx, e.clientY - sy) > 10) release();
    }, { capture: true, passive: true });

    ['pointerup', 'pointercancel', 'dragstart'].forEach((type) =>
      document.addEventListener(type, release, { capture: true, passive: true }));
    window.addEventListener('scroll', release, { capture: true, passive: true });
    window.addEventListener('blur', release);
  }

  /* ------------------------------------------------------------------ *
   * 2. Scroll state — header hairline appears once content runs under it
   * ------------------------------------------------------------------ */
  function initScrollState() {
    let ticking = false;
    const update = () => {
      ticking = false;
      html.classList.toggle('la-scrolled', window.scrollY > 4);
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------ *
   * 3. Cart badge — status feedback with a small spring pop (causality: the add)
   * ------------------------------------------------------------------ */
  function initCartBadge() {
    const read = (el) => parseInt((el.textContent || '').trim(), 10) || 0;
    const last = new WeakMap();
    let lastPop = -Infinity;

    const pop = () => {
      const now = performance.now();
      if (now - lastPop < 300) return;
      lastPop = now;
      haptic(10);
      if (reducedMotion()) return;
      const { frames, duration } = springKeyframes(0.4, 1, { damping: 0.5, response: 0.34 });
      document.querySelectorAll(SEL.cartCount).forEach((el) => {
        if (el.offsetParent !== null) el.animate(frames, { duration, easing: 'linear' });
      });
    };

    const check = () => {
      let increased = false;
      document.querySelectorAll(SEL.cartCount).forEach((el) => {
        const n = read(el);
        const prev = last.has(el) ? last.get(el) : n;
        if (n > prev) increased = true;
        last.set(el, n);
      });
      if (increased) pop();
    };

    document.querySelectorAll(SEL.cartCount).forEach((el) => last.set(el, read(el)));

    // Theme pub/sub (classic-script globals from pubsub.js / context-variables)
    try {
      // eslint-disable-next-line no-undef
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        // eslint-disable-next-line no-undef
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => nextFrame(check));
      }
    } catch (_) { /* theme without pubsub */ }

    // Fallback: watch the header's cart icon for count re-renders
    const icon = document.getElementById('cart-count-icon');
    if (icon) {
      new MutationObserver(() => requestAnimationFrame(check))
        .observe(icon, { childList: true, characterData: true, subtree: true });
    }
  }

  /* ------------------------------------------------------------------ *
   * 4. Kill open latency — the theme waits 400ms between showing a drawer or
   *    popup and starting its transition. Start it on the next frame instead.
   * ------------------------------------------------------------------ */
  const watched = new WeakSet();

  function watchOpen(root) {
    if (watched.has(root) || root.closest(SEL.exclude)) return;
    watched.add(root);
    let wasShown = root.style.display !== 'none' && getComputedStyle(root).display !== 'none';
    new MutationObserver(() => {
      const shown = root.style.display !== 'none' && getComputedStyle(root).display !== 'none';
      if (shown && !wasShown && !root.classList.contains('show')) {
        nextFrame(() => {
          if (root.style.display === 'none' || root.classList.contains('show')) return;
          root.classList.add('show');
          if (root.matches(SEL.drawer)) document.body.classList.add('overflow-hidden');
        });
      }
      if (!shown && root.classList.contains('show')) root.classList.remove('show');
      wasShown = shown;
    }).observe(root, { attributes: true, attributeFilter: ['style'] });
  }

  /* ------------------------------------------------------------------ *
   * 5. Popups — exit along the path they entered (scale + fade), then let the theme close
   * ------------------------------------------------------------------ */
  function initPopupExit() {
    document.addEventListener('click', (e) => {
      if (!e.isTrusted || reducedMotion()) return;
      const t = e.target;
      if (!(t instanceof Element)) return;
      const closer = t.closest(SEL.popupClose);
      if (!closer) return;
      const popup = closer.closest('.popup.show');
      if (!popup || popup.classList.contains('la-closing')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      popup.classList.add('la-closing');
      setTimeout(() => {
        closer.click(); // synthetic: passes straight through to the theme's handler
        popup.classList.remove('la-closing');
      }, 200);
    }, true);
  }

  /* ------------------------------------------------------------------ *
   * 6. Sheets — drag to dismiss with velocity handoff, projection and rubber-banding.
   *    Phones: bottom sheet (vertical). Tablets/desktop touch: side panel (horizontal).
   * ------------------------------------------------------------------ */
  const sheets = new WeakSet();

  function isOpen(root) {
    if (!root.isConnected || !root.classList.contains('show')) return false;
    return getComputedStyle(root).display !== 'none';
  }

  function attachSheet(root) {
    if (sheets.has(root) || root.closest(SEL.exclude)) return;
    // The theme may re-render the drawer's insides (the cart does on every change), so the
    // listeners live on the drawer itself and the panel is looked up again on each touch.
    let panel = root.querySelector(SEL.drawerPanel);
    if (!panel) return;
    sheets.add(root);

    const state = {
      phase: 'idle', // idle | pending | drag | settling
      axis: 'x',
      dir: 1,
      size: 0,
      startX: 0,
      startY: 0,
      grab: 0,
      offset: 0,
      overlay: null,
      pointerId: null,
      swallowClick: false
    };
    const tracker = new VelocityTracker();

    // `offset` is the raw gesture/spring value (+ = toward dismiss). Inward travel is rubber-banded.
    const apply = (offset) => {
      state.offset = offset;
      const visual = offset >= 0 ? offset : -rubberband(-offset, state.size || 300);
      panel.style.translate = state.axis === 'x' ? `${visual * state.dir}px 0` : `0 ${visual * state.dir}px`;
      if (state.overlay) {
        state.overlay.style.opacity = String(1 - clamp(visual / (state.size || 1), 0, 1));
      }
    };

    const spring = new Spring({ damping: 1, response: 0.3, onUpdate: apply });

    const cleanup = () => {
      spring.stop();
      panel.style.removeProperty('translate');
      panel.style.removeProperty('transition');
      if (state.overlay) {
        state.overlay.style.removeProperty('opacity');
        state.overlay.style.removeProperty('transition');
      }
      root.classList.remove('la-dragging');
      state.phase = 'idle';
      state.offset = 0;
    };

    const measure = () => {
      const r = panel.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (r.width === 0 || r.height === 0) return false;
      const touchesRight = r.right >= vw - 2;
      const touchesLeft = r.left <= 2;
      if (touchesLeft && touchesRight) {
        if (r.top < vh * 0.04 || r.bottom < vh - 2) return false; // full-screen: not a sheet
        state.axis = 'y'; state.dir = 1; state.size = r.height;   // bottom sheet
      } else if (touchesLeft) {
        state.axis = 'x'; state.dir = -1; state.size = r.right;
      } else if (touchesRight) {
        state.axis = 'x'; state.dir = 1; state.size = vw - r.left;
      } else {
        return false;
      }
      state.overlay = root.querySelector(SEL.drawerOverlay);
      return true;
    };

    // Vertical sheets: inner content keeps scrolling unless it sits at its top edge
    const scrollerBlocks = (el, deltaY) => {
      for (let n = el; n && n !== panel; n = n.parentElement) {
        const cs = getComputedStyle(n);
        if (/(auto|scroll)/.test(cs.overflowY) && n.scrollHeight > n.clientHeight + 1) {
          if (deltaY < 0) return true;
          if (n.scrollTop > 0) return true;
        }
      }
      return false;
    };

    const horizontalScroller = (el) => {
      for (let n = el; n && n !== panel; n = n.parentElement) {
        const cs = getComputedStyle(n);
        if (/(auto|scroll)/.test(cs.overflowX) && n.scrollWidth > n.clientWidth + 1) return true;
      }
      return false;
    };

    const beginDrag = (e) => {
      state.phase = 'drag';
      state.grab = state.offset; // respect the current (possibly mid-flight) position
      panel.style.transition = 'none';
      if (state.overlay) state.overlay.style.transition = 'none';
      root.classList.add('la-dragging');
      try { panel.setPointerCapture(e.pointerId); } catch (_) { /* ok */ }
    };

    const onDown = (e) => {
      if (!OPT.drag || e.pointerType === 'mouse') return; // touch and pen only
      if (e.button !== 0 || e.isPrimary === false) return;
      if (e.target.closest(SEL.noDrag)) return;
      const hit = e.target.closest(SEL.drawerPanel);
      if (!hit || !root.contains(hit)) return;
      const midFlight = state.phase === 'settling';
      if (!midFlight && hit !== panel) panel = hit;
      if (!midFlight) {
        if (!isOpen(root) || !measure()) return;
        state.offset = 0;
        if (state.axis === 'x' && horizontalScroller(e.target)) return;
      } else {
        spring.stop(); // interruptible: hold the panel exactly where the finger caught it
      }
      state.phase = 'pending';
      state.startX = e.clientX;
      state.startY = e.clientY;
      state.pointerId = e.pointerId;
      tracker.reset();
    };

    const onMove = (e) => {
      if (e.pointerId !== state.pointerId) return;
      if (state.phase !== 'pending' && state.phase !== 'drag') return;
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;

      if (state.phase === 'pending') {
        if (Math.hypot(dx, dy) < 10) return; // hysteresis before committing to a direction
        const held = state.offset !== 0;     // caught mid-flight: any movement drives it
        if (!held) {
          const along = state.axis === 'x' ? dx : dy;
          const across = state.axis === 'x' ? dy : dx;
          if (Math.abs(along) < Math.abs(across) * 1.15) { state.phase = 'idle'; return; } // a scroll
          if (state.axis === 'y' && scrollerBlocks(e.target, dy)) { state.phase = 'idle'; return; }
        }
        state.startX = e.clientX;
        state.startY = e.clientY;
        beginDrag(e);
        return;
      }

      const along = state.axis === 'x' ? dx : dy;
      tracker.add(along * state.dir);
      apply(state.grab + along * state.dir);
    };

    const onUp = (e) => {
      if (e.pointerId !== state.pointerId) return;
      if (state.phase === 'pending') {
        if (state.offset !== 0) settle(0); // caught and released without moving: finish the motion
        else state.phase = 'idle';
        return;
      }
      if (state.phase !== 'drag') return;
      try { panel.releasePointerCapture(e.pointerId); } catch (_) { /* ok */ }
      state.swallowClick = true;
      setTimeout(() => { state.swallowClick = false; }, 0);
      settle(e.type === 'pointercancel' ? 0 : tracker.velocity()); // px/s, + = toward dismiss
    };

    function settle(v) {
      const projected = state.offset + project(v);
      const dismiss = projected > state.size * 0.5 && v > -200;
      state.phase = 'settling';

      if (dismiss) {
        spring.to(state.size, {
          velocity: Math.max(v, 0),
          damping: 1,
          response: 0.26,
          onRest: () => {
            haptic(8);
            // Already off-screen: hand back to the theme, whose own close is now invisible
            panel.style.removeProperty('transition');
            if (state.overlay) state.overlay.style.removeProperty('transition');
            const closer = root.querySelector(SEL.drawerClose) || root.querySelector('[data-close-drawer]');
            if (closer) closer.click();
            const started = performance.now();
            const waitClosed = () => {
              if (getComputedStyle(root).display !== 'none' && performance.now() - started < 1200) {
                requestAnimationFrame(waitClosed);
                return;
              }
              cleanup();
            };
            requestAnimationFrame(waitClosed);
          }
        });
      } else {
        // Snap home carrying the release velocity; critically damped so no gap opens at the edge
        spring.to(0, { velocity: v, damping: 1, response: 0.32, onRest: cleanup });
      }
    }

    root.addEventListener('pointerdown', onDown, { passive: true });
    root.addEventListener('pointermove', onMove, { passive: true });
    root.addEventListener('pointerup', onUp);
    root.addEventListener('pointercancel', onUp);

    // Own the touch once it's ours, so the browser doesn't start a native scroll mid-drag
    root.addEventListener('touchmove', (e) => {
      if (state.phase === 'drag') { if (e.cancelable) e.preventDefault(); return; }
      if (state.phase !== 'pending' || state.axis !== 'y') return;
      const t = e.touches[0];
      if (!t) return;
      const dy = t.clientY - state.startY;
      const dx = t.clientX - state.startX;
      if (dy > 0 && dy > Math.abs(dx) && !scrollerBlocks(e.target, dy) && e.cancelable) e.preventDefault();
    }, { passive: false });

    root.addEventListener('click', (e) => {
      if (state.swallowClick) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    // Closed by the theme mid-gesture (scrim tap, Escape): drop our inline styles
    new MutationObserver(() => {
      if (!isOpen(root) && (state.phase === 'drag' || state.phase === 'pending')) cleanup();
      if (getComputedStyle(root).display === 'none' && panel.style.translate) cleanup();
    }).observe(root, { attributes: true, attributeFilter: ['class', 'style'] });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function scan(scope = document) {
    scope.querySelectorAll(SEL.drawer).forEach((root) => { watchOpen(root); attachSheet(root); });
    scope.querySelectorAll(SEL.popup).forEach(watchOpen);
  }

  function setThemeColor() {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta && !meta.getAttribute('content')) meta.setAttribute('content', '#ffffff');
  }

  const boot = () => {
    initPress();
    initScrollState();
    initCartBadge();
    initPopupExit();
    scan();
    setThemeColor();
    html.classList.add('la-on');
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  // Theme editor: sections are re-rendered in place
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
})();
